/**
 * Project Store
 * Global state management for projects and calculations
 */

import { create } from 'zustand';
import { api, type Project, type CalculationResults } from '../services/api';
import { USE_MOCK_DATA, mockProject, mockCalculationResults } from '../services/mockData';

interface ProjectState {
  // Current project
  currentProject: Project | null;
  currentCalculations: CalculationResults | null;

  // All projects
  projects: Project[];

  // Loading states
  isLoading: boolean;
  isCalculating: boolean;
  error: string | null;

  // Actions
  fetchProjects: () => Promise<void>;
  fetchProject: (id: string) => Promise<void>;
  setCurrentProject: (project: Project | null) => void;
  createProject: (project: Partial<Project>) => Promise<void>;
  updateProject: (id: string, project: Partial<Project>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;

  // Calculations
  runCalculations: (inputs: any) => Promise<void>;
  fetchCalculations: (projectId: string) => Promise<void>;
  clearCalculations: () => void;
  setError: (error: string | null) => void;
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  // Initial state
  currentProject: null,
  currentCalculations: null,
  projects: [],
  isLoading: false,
  isCalculating: false,
  error: null,

  // Fetch all projects
  fetchProjects: async () => {
    set({ isLoading: true, error: null });

    // Use mock data if enabled
    if (USE_MOCK_DATA) {
      setTimeout(() => {
        set({
          projects: [mockProject],
          currentProject: mockProject,
          currentCalculations: mockCalculationResults,
          isLoading: false
        });
      }, 300); // Simulate network delay
      return;
    }

    try {
      const response = await api.getProjects();
      if (response.success && response.data) {
        set({ projects: response.data, isLoading: false });
        // Auto-select first project if available
        if (response.data.length > 0) {
          set({ currentProject: response.data[0] });
          // Try to fetch calculations for the first project
          get().fetchCalculations(response.data[0].id);
        }
      } else {
        set({ error: response.error || 'Failed to fetch projects', isLoading: false });
      }
    } catch (error) {
      console.error('API Error:', error);
      // Fallback to mock data on error
      set({
        projects: [mockProject],
        currentProject: mockProject,
        currentCalculations: mockCalculationResults,
        isLoading: false,
        error: 'Using demo data (API unavailable)'
      });
    }
  },

  // Fetch single project
  fetchProject: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.getProject(id);
      if (response.success && response.data) {
        set({ currentProject: response.data, isLoading: false });
      } else {
        set({ error: response.error || 'Failed to fetch project', isLoading: false });
      }
    } catch (error) {
      set({ error: 'Failed to fetch project', isLoading: false });
    }
  },

  // Set current project
  setCurrentProject: (project: Project | null) => {
    set({ currentProject: project });
  },

  // Create project
  createProject: async (project: Partial<Project>) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.createProject(project);
      if (response.success && response.data) {
        const { projects } = get();
        set({
          currentProject: response.data,
          projects: [...projects, response.data],
          isLoading: false,
        });
      } else {
        set({ error: response.error || 'Failed to create project', isLoading: false });
      }
    } catch (error) {
      set({ error: 'Failed to create project', isLoading: false });
    }
  },

  // Update project
  updateProject: async (id: string, project: Partial<Project>) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.updateProject(id, project);
      if (response.success && response.data) {
        const { projects } = get();
        set({
          currentProject: response.data,
          projects: projects.map((p) => (p.id === id ? response.data! : p)),
          isLoading: false,
        });
      } else {
        set({ error: response.error || 'Failed to update project', isLoading: false });
      }
    } catch (error) {
      set({ error: 'Failed to update project', isLoading: false });
    }
  },

  // Delete project
  deleteProject: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.deleteProject(id);
      if (response.success) {
        const { projects, currentProject } = get();
        set({
          projects: projects.filter((p) => p.id !== id),
          currentProject: currentProject?.id === id ? null : currentProject,
          isLoading: false,
        });
      } else {
        set({ error: response.error || 'Failed to delete project', isLoading: false });
      }
    } catch (error) {
      set({ error: 'Failed to delete project', isLoading: false });
    }
  },

  // Run calculations
  runCalculations: async (inputs: any) => {
    set({ isCalculating: true, error: null });
    try {
      const response = await api.runCalculations(inputs);
      if (response.success && response.data) {
        set({
          currentCalculations: response.data.results,
          isCalculating: false,
        });
      } else {
        set({
          error: response.error || 'Failed to run calculations',
          isCalculating: false,
        });
      }
    } catch (error) {
      set({ error: 'Failed to run calculations', isCalculating: false });
    }
  },

  // Fetch calculations
  fetchCalculations: async (projectId: string) => {
    // Use mock data if enabled
    if (USE_MOCK_DATA) {
      // Mock data is already loaded in fetchProjects, just ensure it's set
      const { currentCalculations } = get();
      if (!currentCalculations) {
        set({ currentCalculations: mockCalculationResults, isLoading: false });
      }
      return;
    }

    set({ isLoading: true, error: null });
    try {
      const response = await api.getCalculationResults(projectId);
      if (response.success && response.data) {
        set({ currentCalculations: response.data, isLoading: false });
      } else {
        set({ currentCalculations: null, isLoading: false });
      }
    } catch (error) {
      set({ currentCalculations: null, isLoading: false });
    }
  },

  // Clear calculations
  clearCalculations: () => {
    set({ currentCalculations: null });
  },

  // Set error
  setError: (error: string | null) => {
    set({ error });
  },
}));
